import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { ExportModalPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { TrackingProperties, track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import ReportsForm from 'modules/employees/lib/forms/Reports'
import { useModule as useTimeoffModule } from 'modules/timeoff/hooks/useModule'

import DateRange, { DATERANGES } from './ExportFields/DateRange'
import TimeoffTypeSelect, { TimeoffExportType } from './TimeoffTypeSelect'

enum ExportType {
  Employees = 'employees',
  Timeoff = 'timeoff',
  WorkedDays = 'worked_days',
}

interface Props {
  onClose: () => void
  contractVersions: ContractVersions
  loading: boolean
}

interface ExportOption {
  label: string
  value: string
}

interface ScheduleTimeoffForm {
  from: string
  to: string
  leave_type_ids: number[]
  employee_ids: number[]
}

interface ActivityTimeoffForm {
  from: string
  to: string
  leave_type_ids: number[]
  employee_ids: number[]
  trim_leave_duration: boolean
}

interface UsedTimeoffForm {
  from: string
  to: string
  employee_ids: number[]
}

interface CompanyTimeoffForm {
  employee_ids: number[]
  reference_date: string
}

interface GenericExportForm {
  reference_date: string
}

const ExportEmployeesModalPage = ({ onClose, contractVersions, loading }: Props) => {
  const { loading: loadingTimeoff, enabled: timeoffEnabled } = useTimeoffModule()
  const { loading: loadingExportData, data } = useQuery(ExportModalPageDocument)
  const canExportEmployees = data?.employees._policies.exportEmployees.can || false
  const canDownloadLeaves = data?.apiCore._policies.accessDownloadLeaves.can || false
  const loadingData = loadingTimeoff || loadingExportData

  const exportOptions = React.useMemo(() => {
    const options: ExportOption[] = []
    if (!loadingData) {
      if (canExportEmployees) {
        options.push({
          label: i18n.t('employees.reports.employees'),
          value: ExportType.Employees,
        })
        options.push({
          label: i18n.t('employees.reports.worked_days'),
          value: ExportType.WorkedDays,
        })
      }

      if (timeoffEnabled && canDownloadLeaves) {
        options.push({
          label: i18n.t('employees.reports.timeoff'),
          value: ExportType.Timeoff,
        })
      }
    }

    return options
  }, [timeoffEnabled, loadingData])

  useUnsafeEffect(() => {
    const [firstOption] = exportOptions
    if (firstOption) {
      reportForm.get('type').set(firstOption.value)
    }
  }, [exportOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  const reportForm = React.useRef(
    new ReportsForm({
      type: exportOptions[0]?.value,
      reference_date: dateToString(moment(), 'backend'),
      daterange: DATERANGES.current_month,
      from: dateToString(moment().startOf('month'), 'backend'),
      to: dateToString(moment().add(1, 'M').startOf('month'), 'backend'),
      employee_ids: [],
      leave_type_ids: [],
      trim_leave_duration: false,
      kind: TimeoffExportType.companyTimeoff,
    })
  ).current

  const trackOnSubmit = (
    exportType: ExportType,
    timeoffType?: TimeoffExportType,
    employeeIds?: number[],
    leaveTypeIds?: number[],
    trimLeaveDuration?: boolean
  ) => {
    const properties: TrackingProperties = {
      export_type: exportType,
    }

    if (timeoffType) {
      properties.export_type_timeoff = timeoffType

      const addEmployeesProperty = [
        TimeoffExportType.activityTimeoff,
        TimeoffExportType.companyTimeoff,
        TimeoffExportType.scheduleTimeoff,
      ].includes(timeoffType)

      const addAbsenceTypesProperty = [
        TimeoffExportType.activityTimeoff,
        TimeoffExportType.scheduleTimeoff,
      ].includes(timeoffType)

      if (addEmployeesProperty) {
        properties.number_of_employees = employeeIds?.length
      }
      if (addAbsenceTypesProperty) {
        properties.number_of_absence_types = leaveTypeIds?.length
      }
      if (timeoffType === TimeoffExportType.activityTimeoff) {
        properties.trimmed_absence = !!trimLeaveDuration
      }
    }

    track(trackingEvents.timeoff.exportEmployeeListSubmit, properties)
  }

  const buildScheduleTimeoffForm = () => {
    const body: ScheduleTimeoffForm = {
      from: dateUIToBackend(reportForm.get('from').value),
      to: dateUIToBackend(reportForm.get('to').value),
      leave_type_ids: reportForm.get('leave_type_ids').value,
      employee_ids: reportForm.get('employee_ids').value,
    }
    return body
  }

  const buildActivityTimeoffForm = () => {
    const body: ActivityTimeoffForm = {
      from: dateUIToBackend(reportForm.get('from').value),
      to: dateUIToBackend(reportForm.get('to').value),
      leave_type_ids: reportForm.get('leave_type_ids').value,
      employee_ids: reportForm.get('employee_ids').value,
      trim_leave_duration: reportForm.get('trim_leave_duration').value,
    }
    return body
  }

  const buildCompanyTimeoffForm = () => {
    const body: CompanyTimeoffForm = {
      employee_ids: reportForm.get('employee_ids').value,
      reference_date: dateUIToBackend(reportForm.get('reference_date').value),
    }
    return body
  }

  const buildUsedTimeoffForm = () => {
    const body: UsedTimeoffForm = {
      from: dateUIToBackend(reportForm.get('from').value),
      to: dateUIToBackend(reportForm.get('to').value),
      employee_ids: reportForm.get('employee_ids').value,
    }
    return body
  }

  const validateCompanyTimeoff = () => {
    if (reportForm.get('employee_ids').value.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.reports.error.employee_ids_empty'),
      })
      return false
    }
    return true
  }

  const validateUsedTimeoff = () => {
    if (reportForm.get('employee_ids').value.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.reports.error.employee_ids_empty'),
      })
      return false
    }
    return true
  }

  const validateScheduleTimeoff = () => {
    if (reportForm.get('employee_ids').value.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.reports.error.employee_ids_empty'),
      })
      return false
    }
    if (reportForm.get('leave_type_ids').value.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.reports.error.leave_types_empty'),
      })
      return false
    }
    return true
  }

  const validateActivityTimeoff = () => {
    if (reportForm.get('employee_ids').value.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.reports.error.employee_ids_empty'),
      })
      return false
    }
    if (reportForm.get('leave_type_ids').value.length === 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('employees.reports.error.leave_types_empty'),
      })
      return false
    }
    return true
  }

  const { running, performAction: onSubmit } = useAsyncAction(
    async () => {
      const type = reportForm.get('type').value
      const api = ApiClient()

      switch (type) {
        case ExportType.Employees:
          trackOnSubmit(type)

          const { result: isDownloadable } = await api.get('/export/employees/download_available')
          if (isDownloadable) {
            window.location.href = buildUrl('/export/employees')
          } else {
            app.setFlash({
              type: 'correct',
              message: i18n.t('employees.reports.email_will_send'),
            })
          }
          break

        case ExportType.Timeoff: {
          const kind = reportForm.get('kind').value

          // eslint-disable-next-line prefer-const
          let body:
            | CompanyTimeoffForm
            | ScheduleTimeoffForm
            | ActivityTimeoffForm
            | UsedTimeoffForm
            | GenericExportForm

          switch (kind) {
            case TimeoffExportType.companyTimeoff:
              if (!validateCompanyTimeoff()) return false
              body = buildCompanyTimeoffForm()
              break
            case TimeoffExportType.usedTimeoff:
              if (!validateUsedTimeoff()) return false
              body = buildUsedTimeoffForm()
              break
            case TimeoffExportType.scheduleTimeoff:
              if (!validateScheduleTimeoff()) return false
              body = buildScheduleTimeoffForm()
              break
            case TimeoffExportType.activityTimeoff:
              if (!validateActivityTimeoff()) return false
              body = buildActivityTimeoffForm()
              break
            default:
              body = { reference_date: dateUIToBackend(reportForm.get('reference_date').value) }
              break
          }

          const { result } = await api.post(`/timeoff/exports/${kind}/request_download`, body)

          if (result) {
            trackOnSubmit(
              type,
              kind,
              reportForm.get('employee_ids').value,
              reportForm.get('leave_type_ids').value,
              reportForm.get('trim_leave_duration').value
            )

            app.setFlash({
              type: 'correct',
              message: i18n.t('employees.reports.email_will_send'),
            })
          }

          break
        }

        case ExportType.WorkedDays: {
          let from, to

          if (reportForm.get('daterange').value === DATERANGES.current_month) {
            from = dateToString(moment().startOf('month'), 'backend')
            to = dateToString(moment(), 'backend')
          } else if (reportForm.get('daterange').value === DATERANGES.current_year) {
            from = dateToString(moment().startOf('year'), 'backend')
            to = dateToString(moment(), 'backend')
          } else {
            from = dateUIToBackend(reportForm.get('from').value)
            to = dateUIToBackend(reportForm.get('to').value)
          }

          const { result } = await api.get(`/export/worked_days?from=${from}&to=${to}`)

          if (result) {
            trackOnSubmit(type)

            window.location.href = buildUrl(`/export/worked_days/download?from=${from}&to=${to}`)
          } else {
            trackOnSubmit(type)

            app.setFlash({
              type: 'correct',
              message: i18n.t('employees.reports.email_will_send'),
            })
          }

          break
        }
      }
    },
    {
      successFlash: false,
      errorFlash: true,
    }
  )

  useEffect(() => {
    track(trackingEvents.timeoff.exportEmployeeListStart)
  }, [])

  return (
    <Modal
      illustration="download"
      title={i18n.t('employees.reports.title')}
      description={i18n.t('employees.reports.description_generic')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={running || loadingData} submit>
          {i18n.t('employees.reports.download')}
        </Button>
      }
    >
      {loadingData || loading ? (
        <Loading />
      ) : (
        <>
          <FormRow>
            <Select
              label={i18n.t('employees.reports.type')}
              field={reportForm.get('type')}
              options={exportOptions}
            />
          </FormRow>
          {reportForm.get('type').value === ExportType.Timeoff && (
            <TimeoffTypeSelect form={reportForm} contractVersions={contractVersions} />
          )}
          {reportForm.get('type').value === ExportType.WorkedDays && (
            <DateRange form={reportForm} />
          )}
        </>
      )}
    </Modal>
  )
}

export default observer(ExportEmployeesModalPage)
